import React, { useState } from 'react';
import useForm from 'react-hook-form';
import toastr from 'toastr';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core';
import FormGeneric from 'components/organisms/FormGeneric';
import CompanyService from 'services/company.service';
import { onlyDigits, isCPF, isCNPJ, isCEP } from 'helpers/documents';

// Tokens do projeto: raio 8, borda #e7ebee, cinzas #909090/#5b616f,
// régua #EDF0F2 e azul #308ACB — os mesmos de stylesInputs e styledForm.
const useFormStyles = makeStyles({
  section: {
    marginTop: 28,
    marginBottom: 4,
    paddingBottom: 8,
    borderBottom: '2px solid #EDF0F2'
  },
  sectionFirst: {
    marginTop: 4
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 'bold',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#909090'
  },
  field: {
    '& label.Mui-focused': { color: '#308ACB' },
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      '& fieldset': { borderColor: '#e7ebee', borderWidth: 2 },
      '&:hover fieldset': { borderColor: '#cfd8dd' },
      '&.Mui-focused fieldset': { borderColor: '#308ACB', borderWidth: 2 }
    }
  },
  optionRow: {
    marginTop: 20,
    padding: '4px 12px',
    border: '2px solid #e7ebee',
    borderRadius: 8
  },
  note: {
    marginTop: 20,
    padding: '14px 16px',
    borderRadius: 8,
    backgroundColor: '#f6f8f9',
    color: '#5b616f',
    lineHeight: 1.6
  }
});

const FormCompanyPage = props => {
  const { register, handleSubmit, errors } = useForm();
  const [requireTripDescription, setRequireTripDescription] = useState(false);
  const [saving, setSaving] = useState(false);

  const classes = useFormStyles();

  const SectionTitle = ({ children, first }) => (
    <Grid
      item
      xs={12}
      className={`${classes.section} ${first ? classes.sectionFirst : ''}`}
    >
      <span className={classes.sectionLabel}>{children}</span>
    </Grid>
  );

  const buildPayload = data => ({
    name: data.name,
    corporateName: data.corporateName,
    cnpj: onlyDigits(data.cnpj),
    email: data.email,
    requireTripDescription,
    address: {
      zipcode: onlyDigits(data.zipcode),
      country: 'Brasil',
      province: data.province,
      city: data.city,
      district: data.district,
      street: data.street,
      unit: data.unit
    },
    // A API aceita `limits` e `costCenters` no corpo mas não persiste nenhum
    // dos dois: ela cria sozinha o limite padrão "Ilimitado". Enviar `limits`
    // ainda faz o admin ser vinculado a um limite sem id, o que quebra o
    // cadastro. Plano e centros de custo são criados depois, pelas telas.
    employees: [
      {
        name: data.adminName,
        email: data.adminEmail,
        cpf: onlyDigits(data.adminCpf),
        phone: onlyDigits(data.adminPhone)
      }
    ],
    billingConfig: [
      {
        creationDay: Number(data.creationDay),
        payWithin: Number(data.payWithin)
      }
    ]
  });

  async function doSubmitCompany(data) {
    if (saving) return;
    setSaving(true);

    const response = await CompanyService.create(buildPayload(data));

    setSaving(false);

    if (response?.status === 201 || response?.status === 200) {
      toastr.success(
        'Empresa cadastrada. O administrador vai receber o e-mail para definir a senha.'
      );
      props.onRefresh();
      props.setToogleScreen(false);
      return;
    }

    if (response?.status === 409) {
      toastr.error('Já existe uma empresa cadastrada com este CNPJ.');
      return;
    }

    toastr.error(
      'Não foi possível cadastrar a empresa. Confira os dados e tente novamente.'
    );
  }

  const field = (name, label, options = {}) => (
    <Grid item xs={options.xs || 6}>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        label={label}
        name={name}
        type={options.type || 'text'}
        className={classes.field}
        error={!!errors[name]}
        helperText={(errors[name] || {}).message}
        inputRef={options.validation ? register(options.validation) : register}
      />
    </Grid>
  );

  return (
    <FormGeneric
      setToogleScreen={props.setToogleScreen}
      isEdition={false}
      onSubmit={handleSubmit(doSubmitCompany)}
      context="empresa"
      title="Adicionar nova empresa"
    >
      <Grid container spacing={2} style={{ margin: '0px' }}>
        <SectionTitle first>Dados da empresa</SectionTitle>

        {field('name', 'Nome fantasia*', {
          validation: {
            required: { value: true, message: 'Preenchimento obrigatório' }
          }
        })}

        {field('corporateName', 'Razão social*', {
          validation: {
            required: { value: true, message: 'Preenchimento obrigatório' }
          }
        })}

        {field('cnpj', 'CNPJ*', {
          validation: {
            required: { value: true, message: 'Preenchimento obrigatório' },
            validate: value => isCNPJ(value) || 'CNPJ inválido'
          }
        })}

        {field('email', 'E-mail da empresa*', {
          validation: {
            required: { value: true, message: 'Preenchimento obrigatório' },
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'E-mail inválido' }
          }
        })}

        <SectionTitle>Endereço</SectionTitle>

        {field('zipcode', 'CEP*', {
          xs: 3,
          validation: {
            required: { value: true, message: 'Preenchimento obrigatório' },
            validate: value => isCEP(value) || 'CEP inválido'
          }
        })}

        {field('street', 'Logradouro*', {
          xs: 6,
          validation: {
            required: { value: true, message: 'Preenchimento obrigatório' }
          }
        })}

        {field('unit', 'Número / complemento', { xs: 3 })}

        {field('district', 'Bairro*', {
          xs: 5,
          validation: {
            required: { value: true, message: 'Preenchimento obrigatório' }
          }
        })}

        {field('city', 'Cidade*', {
          xs: 5,
          validation: {
            required: { value: true, message: 'Preenchimento obrigatório' }
          }
        })}

        {field('province', 'UF*', {
          xs: 2,
          validation: {
            required: { value: true, message: 'Preenchimento obrigatório' },
            maxLength: { value: 2, message: 'Use a sigla' }
          }
        })}

        <SectionTitle>Administrador</SectionTitle>

        {field('adminName', 'Nome completo*', {
          validation: {
            required: { value: true, message: 'Preenchimento obrigatório' },
            validate: value =>
              `${value}`.trim().includes(' ') || 'Informe o nome completo'
          }
        })}

        {field('adminEmail', 'E-mail*', {
          validation: {
            required: { value: true, message: 'Preenchimento obrigatório' },
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'E-mail inválido' }
          }
        })}

        {field('adminCpf', 'CPF*', {
          validation: {
            required: { value: true, message: 'Preenchimento obrigatório' },
            validate: value => isCPF(value) || 'CPF inválido'
          }
        })}

        {field('adminPhone', 'Telefone', {})}

        <SectionTitle>Faturamento</SectionTitle>

        {field('creationDay', 'Dia de fechamento da fatura*', {
          xs: 6,
          type: 'number',
          validation: {
            required: { value: true, message: 'Preenchimento obrigatório' },
            min: { value: 1, message: 'Entre 1 e 28' },
            max: { value: 28, message: 'Entre 1 e 28' }
          }
        })}

        {field('payWithin', 'Prazo de pagamento (dias)*', {
          xs: 6,
          type: 'number',
          validation: {
            required: { value: true, message: 'Preenchimento obrigatório' },
            min: { value: 1, message: 'Informe ao menos 1 dia' }
          }
        })}

        <Grid item xs={12} className={classes.optionRow}>
          <FormControlLabel
            control={
              <Checkbox
                checked={requireTripDescription}
                onChange={e => setRequireTripDescription(e.target.checked)}
                style={{ color: '#308ACB' }}
              />
            }
            label="Exigir justificativa em toda viagem"
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="caption" component="p" className={classes.note}>
            A empresa é criada com status "Lead" e passa a "Ativa" quando o
            administrador define a senha pelo e-mail que receber. O plano de uso
            e os centros de custo são configurados depois, já dentro da empresa.
          </Typography>
        </Grid>
      </Grid>
    </FormGeneric>
  );
};

export default FormCompanyPage;
